import React, { useState, useEffect, useRef, useMemo } from "react";
import GeneralInformationForm from "./Components/GeneralInformation";
import ProgressStep from "./Components/ProgressStepper";
import EventDatePicker from "./Components/EventDatePicker";
import CategorySelection from "./Components/CategorySelection";
import ProductServiceSelection from "./Components/ProductServiceSelection";
import { useCart } from "../../contexts/CartContext";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import RecruitmentModal, {
  hasDismissedRecruitmentModal,
} from "./Components/RecruitmentModal";
import PageHeader from "../../components/controls/PageHeader";
import AnanasSolutionsLogo from "../../images/AnanasSolutionsLogo.webp";
import CartPageShop from "../Cart/CartPageShop";

const DRAFT_KEY = "eventCreationDraft:v1";
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

const nowMs = () => Date.now();

const safeParse = (raw) => {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const isExpired = (savedAt) => {
  if (!savedAt) return true;
  return nowMs() - savedAt > DRAFT_TTL_MS;
};

const readDraft = () => {
  const draft = safeParse(localStorage.getItem(DRAFT_KEY));
  if (!draft) return null;
  if (isExpired(draft.savedAt)) {
    localStorage.removeItem(DRAFT_KEY);
    return null;
  }
  return draft;
};

const writeDraft = (payload) => {
  localStorage.setItem(
    DRAFT_KEY,
    JSON.stringify({ ...payload, savedAt: nowMs() })
  );
};

const clearDraft = () => {
  localStorage.removeItem(DRAFT_KEY);
};

const defaultEventData = {
  attendees: 0,
  budget: 0,
  location: "",
  contactPhone: "",
  selectedCategory: null,
  selectedProducts: [],
  selectedServices: [],
  dates: [{ date: "", time: "", expanded: true }],
};

const EventCreationWizard = () => {
  const { t } = useTranslation();
  const { cart, clearCart } = useCart();

  const [currentStep, setCurrentStep] = useState(1);
  const [maxStepReached, setMaxStepReached] = useState(1);
  const [eventData, setEventData] = useState(defaultEventData);
  const [warningMessage, setWarningMessage] = useState("");
  const [direction, setDirection] = useState(0);
  const [showRecruitmentModal, setShowRecruitmentModal] = useState(
    !hasDismissedRecruitmentModal()
  );

  const steps = useMemo(
    () => [
      t("wizard.steps.info"),
      t("wizard.steps.dates"),
      t("wizard.steps.categories"),
      t("wizard.steps.productsServices"),
      t("wizard.steps.confirmation"),
    ],
    [t]
  );

  const didHydrateRef = useRef(false);
  const firstCartClearDoneRef = useRef(false);
  const saveTimerRef = useRef(null);

  useEffect(() => {
    const draft = readDraft();
    if (!draft) {
      didHydrateRef.current = true;
      return;
    }
    setCurrentStep(draft.currentStep || 1);
    setMaxStepReached(draft.maxStepReached || 1);
    setEventData(draft.eventData || defaultEventData);
    setWarningMessage(draft.warningMessage || "");
    setDirection(0);
    didHydrateRef.current = true;
  }, []);

  useEffect(() => {
    if (!didHydrateRef.current) return;
    if (firstCartClearDoneRef.current) return;
    const hasDraft = !!readDraft();
    if (!hasDraft && cart.length > 0) clearCart();
    firstCartClearDoneRef.current = true;
  }, [cart.length, clearCart]);

  useEffect(() => {
    if (!didHydrateRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      writeDraft({ currentStep, maxStepReached, eventData, warningMessage });
    }, 250);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [currentStep, maxStepReached, eventData, warningMessage]);

  const goToNextStep = () => {
    setCurrentStep((prev) => {
      const next = prev + 1;
      setMaxStepReached((prevMax) => Math.max(prevMax, next));
      return next;
    });
    setDirection(1);
    setWarningMessage("");
  };

  const goToPreviousStep = () => {
    setDirection(-1);
    setCurrentStep((prev) => prev - 1);
  };

  const updateEventData = (data) =>
    setEventData((prev) => ({ ...prev, ...data }));

  const returnToCategorySelection = () => {
    if (currentStep !== 3) setCurrentStep(3);
    setEventData((prev) => ({
      ...prev,
      selectedProducts: [],
      selectedServices: [],
      selectedCategory: null,
    }));
    setTimeout(() => {
      setWarningMessage(t("errors.noProductOrServiceFound"));
      window.scrollTo({ top: 0, behavior: "instant" });
    }, 50);
  };

  const handleStepClick = (targetIndex) => {
    if (targetIndex > maxStepReached) return;
    const diff = targetIndex - currentStep;
    setDirection(diff > 0 ? 1 : -1);
    setCurrentStep(targetIndex);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f8fafc] to-[#e2e8f0] px-4 pb-24">
      <PageHeader
        title={t("homepage.heroSection.cta")}
        subtitle={t("homepage.heroSection.services.createEvent.description")}
        logoSrc={AnanasSolutionsLogo}
        align="center"
        fullBleed={true}
        rounded={false}
        height="sm"
        gradient={{ from: "#E9FBFA", via: "#FFF9E6", to: "#FFE9EC" }}
        patternSize="32px 32px"
        patternOpacity={15}
      />

      <div className="w-full">
        {showRecruitmentModal && (
          <RecruitmentModal onClose={() => setShowRecruitmentModal(false)} />
        )}

        {warningMessage && (
          <div className="text-lg text-semiBold text-red-500 text-center mb-4">
            {warningMessage}
          </div>
        )}

        <div className="flex justify-center overflow-x-auto py-6 px-2 no-scrollbar">
          <div className="min-w-fit">
            <ProgressStep
              currentStep={currentStep}
              steps={steps}
              onStepClick={handleStepClick}
            />
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={`step-${currentStep}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            {currentStep === 1 && (
              <div className="flex justify-center px-4 py-12">
                <div className="w-full max-w-xl">
                  <GeneralInformationForm
                    nextStep={goToNextStep}
                    updateEventData={updateEventData}
                    eventData={eventData}
                    goToPreviousStep={goToPreviousStep}
                  />
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="flex justify-center py-12">
                <div className="w-full max-w-xl">
                  <EventDatePicker
                    nextStep={goToNextStep}
                    updateEventData={updateEventData}
                    eventData={eventData}
                    goToPreviousStep={goToPreviousStep}
                  />
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div>
                <CategorySelection
                  nextStep={goToNextStep}
                  updateEventData={updateEventData}
                  eventData={eventData}
                  goToPreviousStep={goToPreviousStep}
                />
              </div>
            )}

            {currentStep === 4 && (
              <div>
                <ProductServiceSelection
                  nextStep={goToNextStep}
                  updateEventData={updateEventData}
                  eventData={eventData}
                  goToPreviousStep={goToPreviousStep}
                  returnToCategorySelection={returnToCategorySelection}
                />
              </div>
            )}

            {currentStep === 5 && (
              <div>
                <CartPageShop
                  onCompleted={() => {
                    clearDraft();
                    setCurrentStep(1);
                    setMaxStepReached(1);
                    setEventData(defaultEventData);
                    setWarningMessage("");
                  }}
                />
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default EventCreationWizard;
